/* eslint-disable jsx-a11y/accessible-emoji */
/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */

import * as React from "react";
import { I18n } from "i18n-js";
import styled from "styled-components";
import { datadogRum } from "@datadog/browser-rum";
import { format, parseISO } from "date-fns";
import * as locales from "../../../../locales.json";

import * as API from "../../../../api/billing";
import Button from "../../../reusable/button";
import CancelModal from "./cancelModal";
import Card from "../../../reusable/cardv2";
import ErrorMessage from "../../../reusable/errorMessage";
import InfoText from "../../../reusable/infoText";
import Modal from "../../../reusable/reactModal";
import SwitchToAnnualModalBody from "./switchToAnnualModalBody";
import Tooltip from "../../../reusable/tooltip";
import {
  StyledInfoImg,
  StyledLinkSpan,
  StyledPaymentMethodWrapper,
  StyledTableData,
  StyledTableHeader,
  StyledTableRow,
} from "../lib/styles";
import {
  SubscriptionBillingCardPropTypes,
  SubscriptionData,
} from "../lib/types";
import { defaultSubscriptionData } from "../lib/constants";
import { redesignTheme } from "../../../theme/theme";
import PaymentMethod from "./paymentMethod";
import ExplorePlans from "./explore-plans";

const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";

const exclamationTriangle = require("../../../svg/exclamationTriangle.svg");
const infoCircle = require("../../../svg/infoCircle.svg");
const editPencilWhite = require("../../../svg/editPencilWhite.svg");

const StyledPlanLabelPill = styled.span`
  background-color: ${redesignTheme.color.greenAlpha100};
  border-radius: 40px;
  font-size: 12px;
  left: 6px;
  padding: 4px 8px;
  position: relative;
  top: -2px;
`;

const StyledTable = styled.table`
  width: 100%;
  min-width: 75%;
  background-color: transparent;
  line-height: 1.8em;
  margin-bottom: ${redesignTheme.spacing[2.5]};
`;

const StyledContainer = styled.div`
  margin-bottom: ${redesignTheme.spacing[4.5]};
`;

const StyledUsersLink = styled.a`
  background-color: ${redesignTheme.color.gray900};
  border-radius: 100%;
  display: inline-flex;
  margin-left: 6px;
  padding: 3px;
  position: relative;
  top: 1px;
`;

const StyledButton = styled(Button)`
  margin-right: ${redesignTheme.spacing[4.5]};
  margin-bottom: ${redesignTheme.spacing[4.5]};
  display: inline-block;
`;

const StyledButtonNoMarginBottom = styled(Button)`
  margin-right: ${redesignTheme.spacing[4.5]};
  display: inline-block;
`;

const StyledErrorContainer = styled.div`
  margin-bottom: ${redesignTheme.spacing[4.5]};
`;

const StyledMessage = styled.p`
  margin-bottom: ${redesignTheme.spacing[4.5]};
`;

const StyledWarningImg = styled.img`
  height: 16.7px;
  width: 18.2px;
  position: relative;
  top: -2.5px;
  cursor: pointer;
  margin-right: ${redesignTheme.spacing[1.5]};
  display: inline;
`;

const SubscriptionBillingCard = ({
  billingType,
  card,
  openPortal,
  paymentDueOn,
  paymentMethodOnFile,
  refetchData,
  refillAmount,
  refillThreshold,
  setPageLoading,
  setRefetchData,
  showSuperAdminFeature,
  subscriptionAutoCollection,
  subscriptionId,
  subscriptionType,
  updateToastMessage,
  openReactivate,
}: SubscriptionBillingCardPropTypes): React.ReactElement => {
  const [data, setData] = React.useState<SubscriptionData>(
    defaultSubscriptionData
  );
  const [loading, setLoading] = React.useState<boolean>(true);
  const [modal, setModal] = React.useState<string>("");

  const {
    hasProratedBilling,
    isAnnual,
    isAnnualPayingMonthly,
    isMonthly,
    seats,
    seatsMin,
    showDiscount,
    trialing,
  } = data;
  const isCancelled = data.subscriptionStatus === "cancelled";
  const isFlatFee = data.pricingModel === "flat_fee";
  const hasMinSeats = seatsMin !== null && seatsMin >= 1;

  const paymentDueDate =
    paymentDueOn && paymentDueOn !== "--" && paymentDueOn !== "N/A"
      ? format(parseISO(paymentDueOn), "MMM dd, yyyy")
      : "--";
  const trueUpDate = data.subscriptionTrueUpDate
    ? format(parseISO(data.subscriptionTrueUpDate), "MMM dd, yyyy")
    : "--";

  const closeModal = (): void => {
    return setModal("");
  };

  const fetchData = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await API.getSubscriptionBilling();
      setData(response.data.data);
    } catch (error) {
      datadogRum.addError(error);
      setData({ ...defaultSubscriptionData, errorMessage: error.message });
    } finally {
      setLoading(false);
      setRefetchData(false);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  React.useEffect(() => {
    if (refetchData) {
      fetchData();
    }
  }, [refetchData]);

  let userCountTooltip = "_";
  if (!hasProratedBilling) userCountTooltip = "_monthly_";
  if (trialing) userCountTooltip = "_trial_";

  return (
    <Card
      loading={loading}
      subtitle={
        <span
          style={{
            display: "block",
            position: "relative",
            ...(trialing ? { color: redesignTheme.color.green600 } : {}),
          }}
        >
          {isCancelled && (
            <Tooltip
              content={i18n.translate(
                "admin_pages.settings.subscription.deactivation_warning_tooltip"
              )}
              placement="top"
            >
              <StyledWarningImg
                src={exclamationTriangle}
                alt="exclamation triangle"
              />
            </Tooltip>
          )}
          {data.planLabel}
          {trialing && (
            <StyledPlanLabelPill>
              {i18n.translate("admin_pages.settings.subscription.trial")}
            </StyledPlanLabelPill>
          )}
          <ExplorePlans
            paymentMethodOnFile={paymentMethodOnFile}
            subscriptionType={subscriptionType}
            trialing={data.trialing}
            isCancelled={isCancelled}
            openPortal={openPortal}
            openCancel={() => setModal('cancel')}
            openReactivate={openReactivate}
            setRefetchData={setRefetchData}
          />
        </span>
      }
      testId="subscription-billing"
      title={i18n.translate("title.subscription")}
    >
      {data.errorMessage && (
        <StyledErrorContainer>
          <ErrorMessage
            errorMessage={data.errorMessage}
            testId="subscription-billing-error"
          />
        </StyledErrorContainer>
      )}

      {isCancelled && (
        <StyledMessage data-testid="cancelled-message">
          {data.paymentMethodOnFile
            ? i18n.translate(
                "admin_pages.settings.subscription.subscription_cancelled_billing_card"
              )
            : i18n.translate(
                "admin_pages.settings.subscription.subscription_cancelled_no_payment_method"
              )}
        </StyledMessage>
      )}

      <StyledTable>
        <tbody>
          {/* USER COUNT */}
          <StyledTableRow>
            <StyledTableHeader>
              {i18n.translate("admin_pages.settings.subscription.user_count")}{" "}
              <Tooltip
                content={i18n.translate(
                  `admin_pages.settings.subscription.user_count${userCountTooltip}tooltip`
                )}
                placement="top"
              >
                <StyledInfoImg src={infoCircle} alt="info circle" />
              </Tooltip>
            </StyledTableHeader>

            <StyledTableData data-testid="user-count">
              {data.userCount || "--"}
              {trialing && (
                <StyledUsersLink
                  href="/company/users"
                  rel="noreferrer"
                  target="_blank"
                >
                  <img
                    alt={i18n.translate(
                      "admin_pages.settings.subscription.change_users"
                    )}
                    src={editPencilWhite}
                  />
                </StyledUsersLink>
              )}
            </StyledTableData>
          </StyledTableRow>

          {/* MIN SEATS */}
          {!isCancelled &&
            !trialing &&
            !isFlatFee &&
            hasMinSeats &&
            seatsMin >= seats && (
              <StyledTableRow>
                <StyledTableHeader>
                  {i18n.translate(
                    "admin_pages.settings.subscription.minimum_seats"
                  )}{" "}
                  <Tooltip
                    content={i18n.translate(
                      "admin_pages.settings.subscription.minimum_seats_tooltip"
                    )}
                    placement="top"
                  >
                    <StyledInfoImg src={infoCircle} alt="info circle" />
                  </Tooltip>
                </StyledTableHeader>
                <StyledTableData data-testid="minimum-seats">
                  {data.seatsMin || "--"}
                </StyledTableData>
              </StyledTableRow>
            )}

          {/* SEATS */}
          {hasProratedBilling &&
            (!hasMinSeats || (hasMinSeats && seats > seatsMin)) &&
            !isCancelled &&
            !trialing &&
            !isFlatFee && (
              <StyledTableRow>
                <StyledTableHeader>
                  {i18n.translate("admin_pages.settings.subscription.seats")}{" "}
                  <Tooltip
                    content={i18n.translate(
                      "admin_pages.settings.subscription.seats_tooltip"
                    )}
                    placement="top"
                  >
                    <StyledInfoImg src={infoCircle} alt="info circle" />
                  </Tooltip>
                </StyledTableHeader>
                <StyledTableData data-testid="seats">
                  {data.seats || "--"}
                </StyledTableData>
              </StyledTableRow>
            )}

          {/* COUPON DISCOUNT */}
          {showDiscount && (
            <StyledTableRow data-testid="discount-row">
              <StyledTableHeader>{data.discountLabel}</StyledTableHeader>
              <StyledTableData>
                {data.discountCouponName} {data.discountCouponValue}
              </StyledTableData>
            </StyledTableRow>
          )}

          {/* MONTHLY SEAT PRICE */}
          <StyledTableRow data-testid="monthly-seat-price">
            <StyledTableHeader>
              {isFlatFee
                ? i18n.translate(
                    "admin_pages.settings.subscription.monthly_price"
                  )
                : i18n.translate(
                    "admin_pages.settings.subscription.monthly_seat_price"
                  )}
            </StyledTableHeader>
            <StyledTableData>
              {data.subscriptionMonthlySeatPrice}
            </StyledTableData>
          </StyledTableRow>

          {/* CANCELLED & TRIAL ACCOUNT PRICE PER USER */}
          {(isCancelled || (trialing && !card.type)) && (
            <StyledTableRow>
              <StyledTableHeader>
                {isAnnual
                  ? i18n.translate(
                      "admin_pages.settings.subscription.annual_price_per_user"
                    )
                  : i18n.translate(
                      "admin_pages.settings.subscription.monthly_price_per_user"
                    )}
              </StyledTableHeader>
              <StyledTableData data-testid="price-per-user">
                {data.userCostPerPlanInterval || "--"}
              </StyledTableData>
            </StyledTableRow>
          )}

          {/* NEXT PAYMENT */}
          {(!hasProratedBilling || isAnnualPayingMonthly) &&
            !isCancelled &&
            !trialing && (
              <StyledTableRow>
                <StyledTableHeader>
                  {i18n.translate(
                    "admin_pages.settings.subscription.next_payment_date"
                  )}
                </StyledTableHeader>
                <StyledTableData data-testid="next-payment-date">
                  {paymentDueDate}
                </StyledTableData>
              </StyledTableRow>
            )}

          {/* TRUE UP & SUBSCRIPTION RENEWAL */}
          {hasProratedBilling &&
            !isAnnualPayingMonthly &&
            !isCancelled &&
            !!card.type && (
              <>
                {paymentDueDate !== trueUpDate && !isFlatFee && (
                  <StyledTableRow>
                    <StyledTableHeader>
                      {i18n.translate(
                        "admin_pages.settings.subscription.next_true_up_date"
                      )}{" "}
                      <Tooltip
                        content={i18n.translate(
                          "admin_pages.settings.subscription.next_true_up_date_tooltip"
                        )}
                        placement="top"
                      >
                        <StyledInfoImg src={infoCircle} alt="info circle" />
                      </Tooltip>
                    </StyledTableHeader>
                    <StyledTableData data-testid="true-up-date">
                      {trueUpDate}
                    </StyledTableData>
                  </StyledTableRow>
                )}
                <StyledTableRow>
                  <StyledTableHeader>
                    {i18n.translate(
                      `admin_pages.settings.subscription.subscription_${
                        trialing ? "start" : "renewal"
                      }_date`
                    )}
                  </StyledTableHeader>
                  <StyledTableData data-testid="subscription-renewal">
                    {paymentDueDate}
                  </StyledTableData>
                </StyledTableRow>
              </>
            )}

          {/* SUBSCRIPTION TERM */}
          <StyledTableRow>
            <StyledTableHeader>
              {i18n.translate(
                "admin_pages.settings.subscription.subscription_term"
              )}
            </StyledTableHeader>
            <StyledTableData data-testid="subscription-term">
              {data.subscriptionTerm}
            </StyledTableData>
          </StyledTableRow>
        </tbody>
      </StyledTable>

      {isMonthly && !isCancelled && (
        <StyledContainer>
          <StyledButtonNoMarginBottom
            onClick={(): void => {
              return setModal("switchToAnnual");
            }}
            size="small"
            testId="switch-to-annual-button"
            variant="primary"
          >
            {i18n.translate("admin_pages.settings.subscription.pay_annually")}
          </StyledButtonNoMarginBottom>
        </StyledContainer>
      )}

      {showSuperAdminFeature && !isCancelled && !trialing && (
        <StyledButton
          onClick={(): void => {
            return setModal("cancel");
          }}
          size="small"
          testId="cancel-plan-button"
          variant="superAdmin"
        >
          {i18n.translate("admin_pages.settings.billing.cancel_plan")}
        </StyledButton>
      )}

      <InfoText>
        {!trialing && (
          <StyledLinkSpan>
            {i18n.translate(
              "admin_pages.settings.subscription.subscription_billing_questions"
            )}{" "}
            <a
              href="https://help.bonus.ly/en/articles/9275261-paying-for-bonusly-subscriptions"
              rel="noreferrer"
              target="_blank"
            >
              {i18n.translate("subscription_cta.learn_more")}
            </a>
          </StyledLinkSpan>
        )}
      </InfoText>

      <InfoText>
        {isCancelled && (
          <StyledLinkSpan
            style={{
              display: "inline-block",
              marginTop: redesignTheme.spacing[2.5],
            }}
          >
            <a
              href="https://help.bonus.ly/articles/357160-why-can-t-i-add-or-delete-users"
              rel="noreferrer"
              target="_blank"
            >
              {i18n.translate("admin_pages.settings.subscription.change_users")}
            </a>{" "}
            {i18n.translate("admin_pages.settings.subscription.from_account")}
          </StyledLinkSpan>
        )}
      </InfoText>

      <StyledPaymentMethodWrapper>
        <PaymentMethod
          cardType={card.type}
          expiration={card.expiration}
          last4={card.last4}
          fetchData={fetchData}
          openPortal={openPortal}
          paymentDueOn={paymentDueOn}
          paymentMethodOnFile={paymentMethodOnFile}
          refillAmount={refillAmount}
          refillThreshold={refillThreshold}
          rewardBillingType={billingType}
          setPageLoading={setPageLoading}
          setRefetchData={setRefetchData}
          subscriptionAutoCollection={subscriptionAutoCollection}
          subscriptionId={subscriptionId}
          type="subscription"
          updateToastMessage={updateToastMessage}
        />
      </StyledPaymentMethodWrapper>

      <CancelModal
        closeModal={closeModal}
        modal={modal}
        updateToastMessage={updateToastMessage}
      />
      <Modal
        ariaLabel="switch to annual modal"
        close={closeModal}
        showDialog={modal === "switchToAnnual"}
        testId="switch-to-annual-modal"
        title={i18n.translate(
          "admin_pages.settings.subscription.switch_to_annual_title"
        )}
      >
        <SwitchToAnnualModalBody
          closeModal={closeModal}
          fetchData={fetchData}
          updateToastMessage={updateToastMessage}
        />
      </Modal>
    </Card>
  );
};

export default SubscriptionBillingCard;
